import React, { useEffect, useMemo, useState } from 'react';
import { FiX, FiEye, FiTrash2, FiShoppingCart } from 'react-icons/fi';
import { apiFetch } from '../api';
import ChooseCartItemModal from './ChooseCartItemModal';

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 4000,
};

const modalStyle = {
  backgroundColor: '#ffffff',
  width: '95%',
  maxWidth: '420px',
  maxHeight: '80vh',
  borderRadius: '14px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle = {
  padding: '14px 16px',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const listStyle = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  overflowY: 'auto',
};

const itemRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  padding: '12px 16px',
  borderBottom: '1px solid #f1f5f9',
};

// Price is flagged "near max" once it's within 15% of the historical high.
const NEAR_MAX_THRESHOLD = 0.85;

// Price is flagged as a price alert once it drops to (or below) the
// midpoint of the historical min/max range (SHOP-58, corrected in SHOP-60).
const PRICE_ALERT_THRESHOLD = 0.5;

const pillBaseStyle = {
  display: 'inline-block',
  fontSize: '0.75em',
  fontWeight: 700,
  borderRadius: '999px',
  padding: '2px 8px',
  marginTop: '2px',
};

const pillVariants = {
  low: { background: '#dcfce7', color: '#166534' },
  high: { background: '#fee2e2', color: '#991b1b' },
  alert: { background: '#dbeafe', color: '#1e40af' },
  // SHOP-115: no display_override and the backend found nothing active to
  // stand in for the original either - shown instead of a price-range pill.
  unavailable: { background: '#e5e7eb', color: '#4b5563' },
};

const rangeTrackStyle = {
  position: 'relative',
  height: '6px',
  borderRadius: '3px',
  background: 'linear-gradient(to right, #16a34a, #f59e0b, #dc2626)',
  marginTop: '6px',
};

const rangeDotStyle = {
  position: 'absolute',
  top: '50%',
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: '#ffffff',
  border: '2px solid #374151',
  boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
  transform: 'translate(-50%, -50%)',
};

// SHOP-67: pin marking where a cheaper equivalent's price falls within the
// same range bar - visually distinct (color, not shape/position) from the
// watched item's own price dot above.
const cheaperPinStyle = {
  ...rangeDotStyle,
  backgroundColor: '#16a34a',
  border: '2px solid #14532d',
};

const cheaperEquivalentStyle = {
  marginTop: '4px',
  fontSize: '0.8em',
  color: '#166534',
};

const rangeLabelsStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '0.7em',
  color: '#9ca3af',
  marginTop: '2px',
};

// Exported so other components (e.g. the header's three-dot menu badge,
// SHOP-61) can compute the same "price alert" flag from /watchlist/details
// data without duplicating the threshold logic.
export const getPriceStatus = (price, min, max) => {
  // A single recorded price point has min === max === price - there's no
  // actual historical range to compare against, so no pill/badge should
  // fire (mirrors RangeBar's own min === max guard below, which likewise
  // hides itself rather than drawing a zero-width range).
  if (min == null || max == null || min === max) return null;
  if (price <= min + (max - min) * PRICE_ALERT_THRESHOLD) return 'alert';
  if (price <= min) return 'low';
  if (max > 0 && price >= max * NEAR_MAX_THRESHOLD) return 'high';
  return null;
};

// Exported alongside getPriceStatus (SHOP-78) so the watched-items list can
// sort rows by the same clamped 0-1 position that RangeBar uses to place the
// price dot, keeping list order and each row's own dot position in agreement
// by construction. Returns null under the same "no real range" guard as
// getPriceStatus/RangeBar (min/max missing or equal).
export const getRangePercent = (price, min, max) => {
  if (min == null || max == null || min === max) return null;
  const clampedPrice = Math.min(Math.max(price, min), max);
  return (clampedPrice - min) / (max - min);
};

const RangeBar = ({ price, min, max, cheaperPrice, translations, formatCurrency }) => {
  const fraction = getRangePercent(price, min, max);
  if (fraction == null) return null;

  const percent = fraction * 100;
  const cheaperFraction = cheaperPrice == null ? null : getRangePercent(cheaperPrice, min, max);
  const cheaperPercent = cheaperFraction == null ? null : cheaperFraction * 100;

  return (
    <div style={{ marginTop: '4px' }}>
      <div style={rangeTrackStyle}>
        <div style={{ ...rangeDotStyle, left: `${percent}%` }} />
        {cheaperPercent != null && (
          <div data-testid="cheaper-equivalent-pin" style={{ ...cheaperPinStyle, left: `${cheaperPercent}%` }} />
        )}
      </div>
      <div style={rangeLabelsStyle}>
        <span>{translations?.priceRangeMin || 'Min'} {formatCurrency(min)}</span>
        <span>{translations?.priceRangeMax || 'Max'} {formatCurrency(max)}</span>
      </div>
    </div>
  );
};

const WatchedItemsModal = ({
  isOpen,
  onClose,
  translations,
  formatCurrency,
  onRemoveWatchedItem,
  onAddToCart,
  selectedStores,
}) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  // SHOP-68: the watched item currently offering a choice between itself and
  // its cheaper_equivalent - null when no chooser is open. Holding the whole
  // item (not just an id) means the chooser doesn't need to re-look it up.
  const [chooserItem, setChooserItem] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    setIsLoading(true);
    setHasError(false);

    // SHOP-69: best_price/worst_price and cheaper_equivalent are scoped to
    // selectedStores server-side, so this refetches whenever the store
    // selection changes while the modal is open.
    apiFetch('/watchlist/details', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(selectedStores || []),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load watched items');
        return res.json();
      })
      .then((data) => setItems(data || []))
      .catch((err) => {
        console.error('Error loading watched item details:', err);
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  }, [isOpen, selectedStores]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const handleRemove = (productid) => {
    setItems((prev) => prev.filter((item) => item.productid !== productid));
    onRemoveWatchedItem?.(productid);
  };

  // SHOP-68: no cheaper equivalent -> add the watched item directly. With
  // one -> ask which of the two the user wants, rather than guessing.
  // SHOP-71: a display_override means the original isn't actually
  // purchasable right now (inactive, or its store deselected) - add the
  // substitute directly instead, skipping the chooser entirely.
  const handleAddToCartClick = (item) => {
    // SHOP-115: no substitute and the original itself isn't active/in a
    // selected store either - there is nothing purchasable to add here, so
    // don't silently fall through to adding the stale original.
    if (!item.display_override && item.active === false) {
      return;
    }
    if (item.display_override) {
      onAddToCart?.(item.display_override);
    } else if (item.cheaper_equivalent) {
      setChooserItem(item);
    } else {
      onAddToCart?.(item);
    }
  };

  const handleChoose = (chosenItem) => {
    onAddToCart?.(chosenItem);
    setChooserItem(null);
  };

  // SHOP-78: order by each item's own position in its price range history
  // (low -> high), same fraction RangeBar uses for the dot, so the list order
  // and the visual position always agree. Items without a real range (no
  // history yet, or best_price === worst_price) have no meaningful position
  // and sort after every item that has one; Array.prototype.sort is stable,
  // so their relative fetch order is preserved among themselves.
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aDisplay = a.display_override || a;
      const bDisplay = b.display_override || b;
      const aFraction = getRangePercent(aDisplay.price, a.best_price, a.worst_price);
      const bFraction = getRangePercent(bDisplay.price, b.best_price, b.worst_price);
      if (aFraction == null && bFraction == null) return 0;
      if (aFraction == null) return 1;
      if (bFraction == null) return -1;
      return aFraction - bFraction;
    });
  }, [items]);

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3
            style={{
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '1.1em',
              fontWeight: 700,
            }}
          >
            <FiEye />
            {translations?.myWatchedItems || 'My Watched Items'}
          </h3>

          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: '1.2em',
              cursor: 'pointer',
            }}
            aria-label={translations?.modalClose || 'Close'}
          >
            <FiX size={20} />
          </button>
        </div>

        <ul style={listStyle}>
          {isLoading && (
            <li style={{ padding: '16px', color: '#6b7280' }}>
              {translations?.loadingItems || 'Loading Items...'}
            </li>
          )}

          {!isLoading && hasError && (
            <li style={{ padding: '16px', color: '#dc2626' }}>
              {translations?.watchToggleError || "Couldn't update your watch list."}
            </li>
          )}

          {!isLoading && !hasError && sortedItems.length === 0 && (
            <li style={{ padding: '16px', color: '#6b7280' }}>
              {translations?.watchedItemsEmpty || 'No watched items yet.'}
            </li>
          )}

          {!isLoading &&
            !hasError &&
            sortedItems.map((item) => {
              // SHOP-71: display_override stands in for an original that's
              // no longer active-and-in-a-selected-store - name/store/price
              // and everything computed off price (status, RangeBar) show
              // the substitute, while item.productid (used for removal)
              // always stays the originally watched product.
              const displayItem = item.display_override || item;
              // SHOP-115: only meaningful when there's no display_override -
              // an override already stands in for an available item, so it
              // takes the normal price-range pill path below.
              const isUnavailable = !item.display_override && item.active === false;
              const status = isUnavailable
                ? 'unavailable'
                : getPriceStatus(displayItem.price, item.best_price, item.worst_price);
              return (
                <li key={item.productid} style={itemRowStyle}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{displayItem.name}</div>
                    {status && (
                      <div style={{ ...pillBaseStyle, ...pillVariants[status] }}>
                        {status === 'unavailable'
                          ? translations?.watchedItemUnavailable || 'Not available'
                          : status === 'alert'
                          ? translations?.priceAlertPill || 'Price alert'
                          : status === 'low'
                          ? translations?.historicLowPill || 'Historic low'
                          : translations?.historicHighPill || 'Near max price'}
                      </div>
                    )}
                    <div style={{ fontSize: '0.85em', color: '#6b7280', textTransform: 'capitalize' }}>
                      {displayItem.store} · {formatCurrency(displayItem.price)}
                    </div>
                    <RangeBar
                      price={displayItem.price}
                      min={item.best_price}
                      max={item.worst_price}
                      cheaperPrice={item.cheaper_equivalent?.price}
                      translations={translations}
                      formatCurrency={formatCurrency}
                    />
                    {item.cheaper_equivalent && (
                      <div style={cheaperEquivalentStyle}>
                        {translations?.cheaperEquivalentLabel || 'Also available for less'}:{' '}
                        {item.cheaper_equivalent.name} ({item.cheaper_equivalent.store}) ·{' '}
                        {formatCurrency(item.cheaper_equivalent.price)}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => handleAddToCartClick(item)}
                      disabled={isUnavailable}
                      aria-label={translations?.addToCart || 'Add to cart'}
                      title={
                        isUnavailable
                          ? translations?.watchedItemUnavailable || 'Not available'
                          : translations?.addToCart || 'Add to cart'
                      }
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: isUnavailable ? 'not-allowed' : 'pointer',
                        color: isUnavailable ? '#9ca3af' : '#16a34a',
                        padding: '6px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <FiShoppingCart size={18} />
                    </button>

                    <button
                      type="button"
                      onClick={() => handleRemove(item.productid)}
                      aria-label={translations?.removeFromWatchList || 'Remove from watch list'}
                      title={translations?.removeFromWatchList || 'Remove from watch list'}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: '#dc2626',
                        padding: '6px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </div>
                </li>
              );
            })}
        </ul>
      </div>

      {chooserItem && (
        <ChooseCartItemModal
          isOpen
          originalItem={chooserItem}
          equivalentItem={chooserItem.cheaper_equivalent}
          onChoose={handleChoose}
          onCancel={() => setChooserItem(null)}
          translations={translations}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
};

export default WatchedItemsModal;
